// API client for partner dashboard endpoints
import type {
  BatchStatus,
  BatchStatusResponse,
  BatchType,
  PartnerBatchStatusResponse,
} from '@reach/shared-types/partner-batches';
import type {
  AcquireMetricsData,
  EngageMetricsData,
  MeasureMetricsData,
  ReputationMetricsData,
} from '@reach/shared-types/partner-metrics';
import type { SendingDomainStatusEnum } from '@reach/shared-types/channels';
import type {
  ContactSchemaMapping,
  ContactSourceSchema,
  CustomEventSchema,
  FieldExposure,
  LocationSourceSchema,
  PartnerSchemaMappings,
  SenderProfile,
  TransactionSchema,
} from '@reach/shared-types/schema-mappings';
import type { ThemeConfig } from '@reach/shared-types/theme';
import {
  type LoginResponse,
  type OrganizationInvitation,
  type OrganizationMember,
  type OrganizationsResponse,
  type PartnerOrganization,
  type SessionResponse,
  type SwitchOrganizationResponse,
} from '@reach/shared-types/workos-auth';

import { API_ENDPOINTS, REACH_CLIENT_HEADER_VALUE } from './constants.js';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: string;
  message?: string;
}

export interface ApiError extends Error {
  response?: {
    status: number;
    statusText: string;
    data: {
      error?: string;
      message?: string;
      data?: unknown;
      details?: string[];
      errors?: Array<
        | {
            path: Array<string | number>;
            message: string;
          }
        | string
      >;
    } | null;
  };
}

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

/** Normalizes an error envelope without dropping backend string-array validation details. */
export const toApiErrorResponseData = (data: unknown): NonNullable<ApiError['response']>['data'] => {
  if (!isObject(data)) {
    return null;
  }

  return {
    error: typeof data.error === 'string' ? data.error : undefined,
    message: typeof data.message === 'string' ? data.message : undefined,
    data: data.data,
    details: Array.isArray(data.details)
      ? data.details.filter((item): item is string => typeof item === 'string')
      : undefined,
    errors: Array.isArray(data.errors)
      ? data.errors.filter(
          (
            item,
          ): item is
            | string
            | {
                path: Array<string | number>;
                message: string;
              } =>
            typeof item === 'string' ||
            (isObject(item) &&
              typeof item.message === 'string' &&
              Array.isArray(item.path) &&
              item.path.every(pathPart => typeof pathPart === 'string' || typeof pathPart === 'number')),
        )
      : undefined,
  };
};

const getApiErrorMessageFromResponse = (data: unknown): string | null => {
  if (!isObject(data)) {
    return null;
  }

  if (typeof data.message === 'string' && data.message.trim().length > 0) {
    return data.message;
  }

  if (typeof data.error === 'string' && data.error.trim().length > 0) {
    return data.error;
  }

  return null;
};

// Types for API responses
export interface PartnerLocation {
  id: string;
  externalId: string;
  name: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  addressLine3: string | null;
  locality: string | null;
  district: string | null;
  postalCode: string | null;
  countryCode: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  googleBusinessProfilePlaceId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PartnerUser {
  id: string;
  externalId: string;
  email: string | null;
  phone: string | null;
  firstName: string | null;
  lastName: string | null;
  emailOptOut: boolean;
  phoneOptOut: boolean;
  status: string;
  deactivatedAt: string | null;
  externalCreatedDate: string | null;
  createdAt: string;
  updatedAt: string;
  tenantId: string;
  tenantName: string;
}

export interface PartnerResource {
  id: string;
  schemaDefinitionId: string;
  platformId: string;
  businessId: string;
  data: Record<string, unknown>;
  resourceExternalId: string;
  createdAt: string;
  updatedAt: string;
}

export interface Tenant {
  id: string;
  name: string;
  externalId: string;
  website?: string;
  smsOptInImageUrls?: string[] | null;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
  locations: Array<{
    district: string;
    locality: string;
    postalCode: string;
    countryCode: string;
    addressLine1: string;
    addressLine2: string;
    addressLine3?: string;
  }>;
  dataIntegrationCompletedAt?: string;
  websiteTrackingCompletedAt?: string;
  acquireActivatedAt?: string | null;
  sendingDomainStatus?: SendingDomainStatusEnum | null;
  userCount: number;
  branding?: {
    logoUrl?: string | null;
    logoBase64?: string | null;
    brandName?: string | null;
    primaryColor?: string | null;
    secondaryColors?: string[] | null;
    brandTagline?: string | null;
    brandPersonality?: {
      tone?: string | null;
      voice?: string | null;
      values?: string[] | null;
    } | null;
    colorPalette?: Record<string, unknown> | null;
    primaryEmail?: string | null;
  } | null;
  uiDefaults?: {
    emailHelpDialogShow?: boolean;
    engageOnboardingShow?: boolean;
    acquireLearningPhaseDisabled?: boolean;
  } | null;
}

export interface TenantsResponse {
  success: boolean;
  message: string;
  data: {
    results: Tenant[];
    pagination: {
      total: number | null;
      hasNextPage: boolean;
      cursor: string | null;
    };
  };
}

export interface PartnerUsersResponse {
  success: boolean;
  message: string;
  data: {
    results: PartnerUser[];
    pagination: {
      total: number | null;
      hasNextPage: boolean;
      cursor: string | null;
    };
  };
}

export interface Visit {
  id: string;
  createdAt: string;
  campaignId: string | null;
  adPlatform: string | null;
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  referrer: string | null;
  landingPageUrl: string | null;
  visitor_id: string | null;
  ip: string | null;
}

export interface Identification {
  id: string;
  email: string | null;
  phone: string | null;
  visitorId: string | null;
  ip: string | null;
  createdAt: string | null;
  submitted_at: string | null;
  sourceId: string | null;
}

export interface Communication {
  id: string;
  createdAt: string;
  updatedAt: string;
  communicationType: 'EMAIL' | 'SMS';
  recipientEmail: string | null;
  recipientPhone: string | null;
  // Status fields
  delivered: boolean;
  opened: boolean;
  clicked: boolean;
  softBounce: boolean;
  hardBounce: boolean;
  spamComplaint: boolean;
  unsubscribed: boolean;
  // Engagement timestamps
  firstOpenedAt: string | null;
  latestOpenedAt: string | null;
  firstClickedAt: string | null;
  latestClickedAt: string | null;
  // Context
  error: string | null;
}

export interface ResourceBySchema {
  schemaDefinitionId: string;
  schemaName: string;
  count: number;
  earliestCreatedAt: string;
  latestCreatedAt: string;
}

export interface PartnerUserDetailsResponse {
  success: boolean;
  message: string;
  data: {
    user: PartnerUser & {
      subscriptionStatus?: string | null;
      billableEventsCount?: number;
      totalRevenue?: number;
      resourceData?: Record<string, unknown>;
      allResourcesBySchema?: ResourceBySchema[];
    };
    visits: Visit[];
    identifications: Identification[];
    billableEvents: BillableEvent[];
    communications: Communication[];
  };
}

export interface BillableEvent {
  id: string;
  external_id?: string;
  amount: string;
  currency: string;
  created_date: string | null;
  paid_date: string | null;
  due_date: string | null;
}

export interface BillableEventsResponse {
  success: boolean;
  message: string;
  data: {
    data: BillableEvent[];
    pagination: {
      total: number | null;
      hasNextPage: boolean;
      cursor: string | null;
    };
    totals: {
      totalCount: number;
      totalAmount: number;
    };
  };
}

// Re-export types from shared package for convenience
export type Organization = PartnerOrganization;
export type {
  OrganizationInvitation,
  OrganizationMember,
  OrganizationsResponse,
  SwitchOrganizationResponse,
};

export interface Partner {
  id: string;
  hash: string;
  name: string;
  domain: string;
  logoUrl?: string | null;
  themeConfig?: Partial<ThemeConfig>;
  featureCustomizations?: {
    engage?: {
      enablePointOfContactSender?: boolean;
      showDeactivateEmailSmsButton?: boolean;
    };
    hideFeature?: {
      engage?: {
        sms?: boolean;
      };
    };
    enableFeature?: {
      engage?: {
        onboarding?: boolean;
      };
    };
    /** Voice settings authored in other surfaces share this object and must survive round-trips. */
    voice?: {
      informationCollectionPrompt?: string;
      [key: string]: unknown;
    };
  };
  jwtSecret?: string | null;
  emailTemplateOverride?: {
    html: string;
    css: string;
    compiledHtml: string;
  } | null;
  hipaaCompliant?: boolean;
}

export type PartnerSigningKeyStatus = 'active' | 'deactivated';

export interface PartnerSigningKey {
  id: string;
  status: PartnerSigningKeyStatus;
  createdAt: string;
  updatedAt: string;
  deactivatedAt: string | null;
}

export interface AuthResponse {
  success: boolean;
  data: LoginResponse;
}

export interface ApiKey {
  key: string;
  createdAt: string;
  lastUsedAt?: string;
}

export interface ApiSecret {
  secret: string;
  createdAt: string;
}

export interface ApiCredentials {
  apiKey: ApiKey;
  apiSecret: ApiSecret;
}

// Re-export metrics types from shared package for convenience
export type { AcquireMetricsData, EngageMetricsData, MeasureMetricsData, ReputationMetricsData };

// Schema Definition types
export interface FieldToExpose {
  name: string;
  description?: string;
  jsonPath: string;
  exposure: FieldExposure[];
}

export interface SchemaDefinition {
  id: string;
  platform_id: string;
  name: string;
  plural_name: string;
  pluralName: string;
  description: string | null;
  category: string;
  version_number: number;
  versionNumber: number;
  data_connection_id?: string;
  dataConnectionId?: string;
  source_query?: string;
  sourceQuery?: string;
  reference_schemas: string[] | null;
  referenceSchemas: string[] | null;
  schema: Record<string, unknown>;
  external_id_field: string;
  externalIdField: string;
  deactivated_at?: string | null;
  deactivatedAt?: string | null;
  created_at: string;
  createdAt: string;
  updated_at: string;
  updatedAt: string;
  fields_to_expose?: FieldToExpose[];
  fieldsToExpose?: FieldToExpose[];
  exposeNameJsonPath?: string;
}

export interface SchemaDefinitionCreate {
  name: string;
  plural_name?: string;
  pluralName?: string;
  description: string;
  category: string;
  external_id_field?: string;
  externalIdField?: string;
  schema: Record<string, unknown>;
  fieldsToExpose?: Record<string, unknown>;
  exposeNameJsonPath?: string;
}

export interface SchemaDefinitionUpdate {
  description?: string;
  schema?: Record<string, unknown>;
  fieldsToExpose?: FieldToExpose[];
  exposeNameJsonPath?: string;
  externalIdField?: string;
  category?: string;
}

// Re-export schema mapping types from shared package for convenience
export type {
  ContactSchemaMapping,
  ContactSourceSchema,
  CustomEventSchema,
  LocationSourceSchema,
  PartnerSchemaMappings as SchemaMappings,
  SenderProfile,
  TransactionSchema,
};

class PartnerApiClient {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    const response = await fetch(url, {
      ...options,
      credentials: 'include', // Include cookies for WorkOS session
      headers: {
        'Content-Type': 'application/json',
        'X-Reach-Client': REACH_CLIENT_HEADER_VALUE,
        ...options.headers,
      },
    });

    // Handle 401 Unauthorized - redirect to login since backend handles refresh automatically
    if (response.status === 401) {
      // Don't redirect if this is already an auth endpoint
      const isAuthEndpoint =
        endpoint.includes('/api/auth/refresh') ||
        endpoint.includes('/api/auth/session') ||
        endpoint.includes('/api/auth/login') ||
        endpoint.includes('/api/auth/logout');

      if (!isAuthEndpoint) {
        // Clear any existing auth state and redirect to login
        window.location.href = `${this.baseUrl}/api/auth/logout`;
        throw new Error('Session expired. Please log in again.');
      }
    }

    if (!response.ok) {
      // Try to parse error response data
      let errorData: unknown;
      try {
        errorData = await response.json();
      } catch {
        errorData = null;
      }

      const errorMessage =
        getApiErrorMessageFromResponse(errorData) ??
        `API request failed: ${response.status} ${response.statusText}`;

      // Create an error object that preserves the response data
      const error = new Error(errorMessage) as ApiError;
      error.response = {
        status: response.status,
        statusText: response.statusText,
        data: toApiErrorResponseData(errorData),
      };
      throw error;
    }

    if (response.status === 204) {
      return undefined as T;
    }

    const responseText = await response.text();
    if (!responseText) {
      return undefined as T;
    }

    return JSON.parse(responseText) as T;
  }

  // Partner endpoints
  async getPartner(): Promise<Partner> {
    const response = await this.request<ApiResponse<Partner>>(API_ENDPOINTS.PARTNER.GET_PROFILE);
    return response.data;
  }

  async getPartnerSigningKeys(): Promise<PartnerSigningKey[]> {
    const response = await this.request<ApiResponse<{ results: PartnerSigningKey[] }>>(
      API_ENDPOINTS.PARTNER.GET_SIGNING_KEYS,
    );
    return response.data.results;
  }

  async createPartnerSigningKey(): Promise<PartnerSigningKey & { rawSecret: string }> {
    const response = await this.request<ApiResponse<PartnerSigningKey & { rawSecret: string }>>(
      API_ENDPOINTS.PARTNER.CREATE_SIGNING_KEY,
      {
        method: 'POST',
      },
    );
    return response.data;
  }

  async deactivatePartnerSigningKey(keyId: string): Promise<PartnerSigningKey> {
    const response = await this.request<ApiResponse<PartnerSigningKey>>(
      API_ENDPOINTS.PARTNER.DEACTIVATE_SIGNING_KEY(keyId),
      {
        method: 'PATCH',
        body: JSON.stringify({
          status: 'deactivated',
        }),
      },
    );
    return response.data;
  }

  async getTenants(
    includeDeleted = false,
    options?: {
      limit?: number;
      cursor?: string;
      search?: string;
      orderBy?: 'name' | 'created_at' | 'updated_at';
      orderDirection?: 'asc' | 'desc';
      fields?: string[];
    },
  ): Promise<TenantsResponse> {
    const params = new URLSearchParams();

    if (includeDeleted) {
      params.append('includeDeleted', 'true');
    }
    if (options?.fields && options.fields.length > 0) {
      params.append('fields', options.fields.join(','));
    }

    if (options?.limit) {
      params.append('limit', options.limit.toString());
    }

    if (options?.cursor) {
      params.append('cursor', options.cursor);
    }

    if (options?.search) {
      params.append('search', options.search);
    }

    if (options?.orderBy) {
      params.append('orderBy', options.orderBy);
    }

    if (options?.orderDirection) {
      params.append('orderDirection', options.orderDirection);
    }

    const queryString = params.toString();
    const url = queryString ? `/partner/tenants?${queryString}` : '/partner/tenants';

    return this.request<TenantsResponse>(url);
  }

  async getTenant(id: string, includeDeleted = true): Promise<Tenant> {
    const params = includeDeleted ? '?includeDeleted=true' : '';
    const response = await this.request<ApiResponse<Tenant>>(`/partner/tenants/${id}${params}`);
    return response.data;
  }

  async getTenantResourceStats(tenantId: string): Promise<{
    resources: ResourceBySchema[];
  }> {
    const response = await this.request<ApiResponse<{ resources: ResourceBySchema[] }>>(
      `/partner/tenants/${tenantId}/resource-stats`,
    );
    return response.data;
  }

  async getTenantSmsRegistrationStatus(tenantId: string): Promise<{
    id: string | null;
    status: string | null;
    application: {
      type?: string;
      company?: {
        name?: string;
        address1?: string;
        address2?: string;
        city?: string;
        stateProvince?: string;
        zipCode?: string;
        country?: string;
      };
      contact?: {
        firstName?: string;
        lastName?: string;
        email?: string;
        phoneNumber?: string;
      };
      messagingUseCase?: {
        category?: string;
        useCaseDetails?: string;
        monthlyVolume?: number;
      };
      messageSamples?: string[];
      tollFreeNumber?: string;
      [key: string]: unknown;
    } | null;
    createdAt: string | null;
    updatedAt: string | null;
  }> {
    const response = await this.request<
      ApiResponse<{
        id: string | null;
        status: string | null;
        application: {
          type?: string;
          company?: {
            name?: string;
            address1?: string;
            address2?: string;
            city?: string;
            stateProvince?: string;
            zipCode?: string;
            country?: string;
          };
          contact?: {
            firstName?: string;
            lastName?: string;
            email?: string;
            phoneNumber?: string;
          };
          messagingUseCase?: {
            category?: string;
            useCaseDetails?: string;
            monthlyVolume?: number;
          };
          messageSamples?: string[];
          tollFreeNumber?: string;
          [key: string]: unknown;
        } | null;
        createdAt: string | null;
        updatedAt: string | null;
      }>
    >(`/partner/tenants/${tenantId}/sms-registration`);
    return response.data;
  }

  async updateTenant(
    id: string,
    data: {
      name?: string;
      website?: string;
      dataIntegrationCompletedAt?: Date;
      websiteTrackingCompletedAt?: Date;
      acquireActivatedAt?: Date | null;
      uiDefaults?: {
        emailHelpDialogShow?: boolean;
        engageOnboardingShow?: boolean;
        acquireLearningPhaseDisabled?: boolean;
      };
      branding?: {
        primaryEmail?: string | null;
      };
    },
  ): Promise<Tenant> {
    const response = await this.request<ApiResponse<Tenant>>(`/partner/tenants/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
    return response.data;
  }

  async deactivateTenant(id: string): Promise<void> {
    await this.request(`/partner/tenants/${id}`, {
      method: 'DELETE',
    });
  }

  async reactivateTenant(id: string): Promise<void> {
    await this.request(`/partner/tenants/${id}/reactivate`, {
      method: 'POST',
    });
  }

  async getTenantLocations(tenantId: string): Promise<PartnerLocation[]> {
    const response = await this.request<ApiResponse<{ locations: PartnerLocation[] }>>(
      `/partner/locations/${tenantId}`,
    );
    return response.data.locations;
  }

  async getTenantPartnerUsers(
    tenantId: string,
    options?: {
      limit?: number;
      cursor?: string;
      search?: string;
      status?: string;
    },
  ): Promise<PartnerUsersResponse> {
    const params = new URLSearchParams();
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.cursor) params.append('cursor', options.cursor);
    if (options?.search) params.append('search', options.search);
    if (options?.status) params.append('status', options.status);

    const queryString = params.toString();
    const url = `/api/partner-users${queryString ? `?${queryString}` : ''}`;

    return this.request<PartnerUsersResponse>(url, {
      headers: {
        'reach-tenant-id': tenantId,
      },
    });
  }

  async getPartnerUsers(options?: {
    limit?: number;
    cursor?: string;
    search?: string;
    status?: string;
    tenantId?: string;
  }): Promise<PartnerUsersResponse> {
    const params = new URLSearchParams();
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.cursor) params.append('cursor', options.cursor);
    if (options?.search) params.append('search', options.search);
    if (options?.status) params.append('status', options.status);
    if (options?.tenantId) params.append('tenantId', options.tenantId);

    const queryString = params.toString();
    const url = `/partner/users${queryString ? `?${queryString}` : ''}`;

    return this.request<PartnerUsersResponse>(url);
  }

  async getTenantPartnerUserDetails(
    externalId: string,
    tenantId: string,
  ): Promise<PartnerUserDetailsResponse> {
    return this.request<PartnerUserDetailsResponse>(`/api/partner-users/${externalId}/details`, {
      headers: {
        'reach-tenant-id': tenantId,
      },
    });
  }

  async getTenantUserResourceRelationships(
    externalId: string,
    tenantId: string,
  ): Promise<
    ApiResponse<{
      primaryUserSchemaName: string;
      inboundReferences: Array<{
        schemaId: string;
        schemaName: string;
        count: number;
        referenceField: string;
        resourceIds: string[];
        category: string;
      }>;
      outboundReferences: Array<{
        schemaId: string;
        schemaName: string;
        count: number;
        referenceField: string;
        resourceIds: string[];
        category: string;
      }>;
    }>
  > {
    return this.request<
      ApiResponse<{
        primaryUserSchemaName: string;
        inboundReferences: Array<{
          schemaId: string;
          schemaName: string;
          count: number;
          referenceField: string;
          resourceIds: string[];
          category: string;
        }>;
        outboundReferences: Array<{
          schemaId: string;
          schemaName: string;
          count: number;
          referenceField: string;
          resourceIds: string[];
          category: string;
        }>;
      }>
    >(`/api/partner-users/${externalId}/relationships`, {
      headers: {
        'reach-tenant-id': tenantId,
      },
    });
  }

  async getTenantBillableEvents(
    tenantId: string,
    options?: {
      limit?: number;
      cursor?: string;
      searchTerm?: string;
      status?: string;
      startDate?: string;
      endDate?: string;
      sortField?: string;
      sortDirection?: string;
    },
  ): Promise<BillableEventsResponse> {
    const params = new URLSearchParams();
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.cursor) params.append('cursor', options.cursor);
    if (options?.searchTerm) params.append('searchTerm', options.searchTerm);
    if (options?.status) params.append('status', options.status);
    if (options?.startDate) params.append('startDate', options.startDate);
    if (options?.endDate) params.append('endDate', options.endDate);
    if (options?.sortField) params.append('sortField', options.sortField);
    if (options?.sortDirection) params.append('sortDirection', options.sortDirection);

    const queryString = params.toString();
    const url = `/api/orders/billable-events${queryString ? `?${queryString}` : ''}`;

    return this.request<BillableEventsResponse>(url, {
      headers: {
        'reach-tenant-id': tenantId,
      },
    });
  }

  async getPartnerResourceByExternalId(
    tenantId: string,
    externalId: string,
    schemaDefinitionId: string,
  ): Promise<PartnerResource> {
    console.log('getPartnerResourceByExternalId called with:', {
      tenantId,
      externalId,
      schemaDefinitionId,
    });

    const response = await this.request<ApiResponse<{ result: PartnerResource }>>(
      `/api/resources/${schemaDefinitionId}/${externalId}`,
      {
        headers: {
          'reach-tenant-id': tenantId,
        },
      },
    );

    console.log('getPartnerResourceByExternalId response:', response);

    if (!response.data || !response.data.result) {
      throw new Error('Resource not found');
    }

    return response.data.result;
  }

  async getPrimaryContactSchemaId(): Promise<{
    primaryUserSchemaId: string | null;
  }> {
    const response = await this.request<{
      data: { primaryUserSchemaId: string | null };
    }>('/partner/me');
    return { primaryUserSchemaId: response.data.primaryUserSchemaId };
  }

  // Organization endpoints
  async getOrganizations(params?: {
    limit?: number;
    before?: string;
    after?: string;
  }): Promise<OrganizationsResponse> {
    const queryParams = new URLSearchParams();
    if (params?.limit) {
      queryParams.append('limit', params.limit.toString());
    }
    if (params?.before) {
      queryParams.append('before', params.before);
    }
    if (params?.after) {
      queryParams.append('after', params.after);
    }
    const queryString = queryParams.toString();
    const url = `/partner/organizations${queryString ? `?${queryString}` : ''}`;
    return this.request<OrganizationsResponse>(url);
  }

  async switchOrganization(organizationId: string): Promise<SwitchOrganizationResponse> {
    return this.request<SwitchOrganizationResponse>('/partner/organizations/switch', {
      method: 'POST',
      body: JSON.stringify({ organizationId }),
    });
  }

  // Team management endpoints
  async getTeamMembers(params?: { limit?: number; before?: string; after?: string }): Promise<{
    results: OrganizationMember[];
    pagination: {
      hasNextPage: boolean;
      hasPreviousPage: boolean;
      cursor: string | null;
      previousCursor: string | null;
    };
  }> {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.before) queryParams.append('before', params.before);
    if (params?.after) queryParams.append('after', params.after);
    const queryString = queryParams.toString();
    const url = `/partner/team/members${queryString ? `?${queryString}` : ''}`;
    const response = await this.request<
      ApiResponse<{
        results: OrganizationMember[];
        pagination: {
          hasNextPage: boolean;
          hasPreviousPage: boolean;
          cursor: string | null;
          previousCursor: string | null;
        };
      }>
    >(url);
    return response.data;
  }

  async getTeamInvitations(params?: { limit?: number; before?: string; after?: string }): Promise<{
    results: OrganizationInvitation[];
    pagination: {
      hasNextPage: boolean;
      hasPreviousPage: boolean;
      cursor: string | null;
      previousCursor: string | null;
    };
  }> {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.before) queryParams.append('before', params.before);
    if (params?.after) queryParams.append('after', params.after);
    const queryString = queryParams.toString();
    const url = `/partner/team/invitations${queryString ? `?${queryString}` : ''}`;
    const response = await this.request<
      ApiResponse<{
        results: OrganizationInvitation[];
        pagination: {
          hasNextPage: boolean;
          hasPreviousPage: boolean;
          cursor: string | null;
          previousCursor: string | null;
        };
      }>
    >(url);
    return response.data;
  }

  async sendTeamInvitation(email: string): Promise<OrganizationInvitation> {
    const response = await this.request<ApiResponse<OrganizationInvitation>>(
      '/partner/team/invitations',
      {
        method: 'POST',
        body: JSON.stringify({ email }),
      },
    );
    return response.data;
  }

  async revokeTeamInvitation(invitationId: string): Promise<OrganizationInvitation> {
    const response = await this.request<ApiResponse<OrganizationInvitation>>(
      `/partner/team/invitations/${invitationId}`,
      {
        method: 'DELETE',
      },
    );
    return response.data;
  }

  async deactivateTeamMember(membershipId: string): Promise<OrganizationMember> {
    return this.updateTeamMemberStatus(membershipId, 'inactive');
  }

  async reactivateTeamMember(membershipId: string): Promise<OrganizationMember> {
    return this.updateTeamMemberStatus(membershipId, 'active');
  }

  private async updateTeamMemberStatus(
    membershipId: string,
    status: 'active' | 'inactive',
  ): Promise<OrganizationMember> {
    const response = await this.request<ApiResponse<OrganizationMember>>(
      `/partner/team/members/${membershipId}`,
      {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      },
    );
    return response.data;
  }

  // Auth endpoints
  async getAuthorizationUrl(organizationId?: string): Promise<AuthResponse> {
    const body = organizationId ? { organizationId } : {};
    return this.request<AuthResponse>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  // Session management - all use httpOnly cookies
  async validateSession(): Promise<SessionResponse> {
    const response = await this.request<ApiResponse<SessionResponse>>('/api/auth/session', {
      method: 'GET',
    });
    return response.data;
  }

  // Partner Settings endpoints
  async updatePartnerProfile(data: Partial<Partner>): Promise<Partner> {
    const response = await this.request<ApiResponse<Partner>>('/partner/profile', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
    return response.data;
  }

  // Schema Definition endpoints
  async getSchemaDefinitions(): Promise<SchemaDefinition[]> {
    const response = await this.request<ApiResponse<SchemaDefinition[]>>('/partner/schema-definitions');
    return response.data;
  }

  async getSchemaDefinition(schemaIdOrName: string): Promise<SchemaDefinition> {
    const response = await this.request<ApiResponse<SchemaDefinition>>(
      `/partner/schema-definitions/${schemaIdOrName}`,
    );
    return response.data;
  }

  async createSchemaDefinition(data: SchemaDefinitionCreate): Promise<SchemaDefinition> {
    const response = await this.request<ApiResponse<SchemaDefinition>>('/partner/schema-definitions', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data;
  }

  async updateSchemaDefinition(
    schemaIdOrName: string,
    data: SchemaDefinitionUpdate,
  ): Promise<SchemaDefinition> {
    const response = await this.request<ApiResponse<SchemaDefinition>>(
      `/partner/schema-definitions/${schemaIdOrName}`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
    );
    return response.data;
  }

  async deleteSchemaDefinition(schemaIdOrName: string): Promise<SchemaDefinition> {
    const response = await this.request<ApiResponse<SchemaDefinition>>(
      `/partner/schema-definitions/${schemaIdOrName}`,
      {
        method: 'DELETE',
      },
    );
    return response.data;
  }

  // Schema Mappings endpoints
  async getSchemaMappings(): Promise<PartnerSchemaMappings> {
    const response = await this.request<ApiResponse<PartnerSchemaMappings>>('/partner/schema-mappings');
    return response.data;
  }

  async updateSchemaMappings(data: PartnerSchemaMappings): Promise<PartnerSchemaMappings> {
    const response = await this.request<ApiResponse<PartnerSchemaMappings>>('/partner/schema-mappings', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data;
  }

  // Resource Counts endpoints
  async getResourceCountsForPartner(options?: {
    cursor?: string;
    limit?: number;
    tenantExternalId?: string | null;
  }): Promise<{
    results: Array<{
      tenantExternalId: string | null;
      schemaDefinitionId: string;
      schemaDefinitionName: string;
      count: number;
    }>;
    pagination: {
      total: number;
      hasNextPage: boolean;
      cursor: string | null;
    };
    aggregates: {
      totalResources: number;
      totalTenants: number;
      totalSchemas: number;
    };
  }> {
    const params = new URLSearchParams();
    if (options?.cursor) {
      params.append('cursor', options.cursor);
    }
    if (options?.limit) {
      params.append('limit', options.limit.toString());
    }
    if (options?.tenantExternalId !== undefined) {
      params.append('tenantExternalId', options.tenantExternalId || '');
    }

    const queryString = params.toString();
    const url = queryString ? `/partner/resources/counts?${queryString}` : '/partner/resources/counts';

    const response = await this.request<
      ApiResponse<{
        results: Array<{
          tenantExternalId: string | null;
          schemaDefinitionId: string;
          schemaDefinitionName: string;
          count: number;
        }>;
        pagination: {
          total: number;
          hasNextPage: boolean;
          cursor: string | null;
        };
        aggregates: {
          totalResources: number;
          totalTenants: number;
          totalSchemas: number;
        };
      }>
    >(url);
    return response.data;
  }

  // Metrics endpoints
  async getEngageMetrics(days: number = 30): Promise<EngageMetricsData> {
    const response = await this.request<ApiResponse<EngageMetricsData>>(
      `/partner/metrics/engage?days=${days}`,
    );
    return response.data;
  }

  async getAcquireMetrics(days: number = 30): Promise<AcquireMetricsData> {
    const response = await this.request<ApiResponse<AcquireMetricsData>>(
      `/partner/metrics/acquire?days=${days}`,
    );
    return response.data;
  }

  async getMeasureMetrics(days: number = 30): Promise<MeasureMetricsData> {
    const response = await this.request<ApiResponse<MeasureMetricsData>>(
      `/partner/metrics/measure?days=${days}`,
    );
    return response.data;
  }

  async getReputationMetrics(): Promise<ReputationMetricsData> {
    const response = await this.request<ApiResponse<ReputationMetricsData>>(
      '/partner/metrics/reputation',
    );
    return response.data;
  }

  // Communications endpoints
  async getCommunications(filters: CommunicationsFilters = {}): Promise<CommunicationsResponse> {
    const params = new URLSearchParams();

    if (filters.type) params.append('type', filters.type);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.platforms) params.append('platforms', filters.platforms);
    if (filters.businesses) params.append('businesses', filters.businesses);
    if (filters.automationType) params.append('automationType', filters.automationType);
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    if (filters.delivered) params.append('delivered', filters.delivered);
    if (filters.opened) params.append('opened', filters.opened);
    if (filters.clicked) params.append('clicked', filters.clicked);
    if (filters.search) params.append('search', filters.search);

    const queryString = params.toString();
    const url = `/partner/communications${queryString ? `?${queryString}` : ''}`;

    const response = await this.request<ApiResponse<CommunicationsResponse>>(url);
    return response.data;
  }

  async getCommunication(id: string, type: 'email' | 'sms' = 'email'): Promise<Communication> {
    const response = await this.request<ApiResponse<Communication>>(
      `/partner/communications/${id}?type=${type}`,
    );
    return response.data;
  }

  async getCommunicationRecipients(
    communicationGroupId: string,
    options?: {
      page?: number;
      limit?: number;
    },
  ): Promise<CommunicationRecipientsResponse> {
    const params = new URLSearchParams();
    if (options?.page) params.append('page', options.page.toString());
    if (options?.limit) params.append('limit', options.limit.toString());

    const queryString = params.toString();
    const url = `/partner/communications/${communicationGroupId}/recipients${queryString ? `?${queryString}` : ''}`;

    const response = await this.request<ApiResponse<CommunicationRecipientsResponse>>(url);
    return response.data;
  }

  async getBusinesses(platformId?: string): Promise<Business[]> {
    const params = platformId ? `?platform=${platformId}` : '';
    const response = await this.request<ApiResponse<{ results: Business[] }>>(
      `/partner/tenants${params}`,
    );
    return response.data.results;
  }

  // Batch endpoints
  async getBatches(
    tenantId: string,
    options?: {
      limit?: number;
      cursor?: string;
      batchType?: BatchType;
      sortBy?: 'createdAt' | 'startedAt' | 'completedAt';
      sortDirection?: 'asc' | 'desc';
    },
  ): Promise<BatchesResponse> {
    const params = new URLSearchParams();
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.cursor) params.append('cursor', options.cursor);
    if (options?.batchType) params.append('batchType', options.batchType);
    if (options?.sortBy) params.append('sortBy', options.sortBy);
    if (options?.sortDirection) params.append('sortDirection', options.sortDirection);

    const queryString = params.toString();
    const url = `/api/batches${queryString ? `?${queryString}` : ''}`;

    return this.request<BatchesResponse>(url, {
      headers: {
        'reach-tenant-id': tenantId,
      },
    });
  }

  async getPartnerBatches(options?: {
    limit?: number;
    cursor?: string;
    tenantId?: string;
    batchId?: string;
    batchType?: BatchType;
    status?: BatchStatus;
    sortBy?: 'createdAt' | 'startedAt' | 'completedAt';
    sortDirection?: 'asc' | 'desc';
  }): Promise<BatchesResponse> {
    const params = new URLSearchParams();
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.cursor) params.append('cursor', options.cursor);
    if (options?.tenantId) params.append('tenantId', options.tenantId);
    if (options?.batchId) params.append('batchId', options.batchId);
    if (options?.batchType) params.append('batchType', options.batchType);
    if (options?.status) params.append('status', options.status);
    if (options?.sortBy) params.append('sortBy', options.sortBy);
    if (options?.sortDirection) params.append('sortDirection', options.sortDirection);

    const queryString = params.toString();
    const url = `/partner/batches${queryString ? `?${queryString}` : ''}`;

    return this.request<BatchesResponse>(url);
  }

  async getBatchStatus(batchId: string, tenantId: string): Promise<BatchStatusResponse> {
    const response = await this.request<ApiResponse<BatchStatusResponse>>(`/api/batches/${batchId}`, {
      headers: {
        'reach-tenant-id': tenantId,
      },
    });
    return response.data;
  }

  // Tool definitions
  async getTools(opts?: { status?: 'active' | 'inactive'; cursor?: string; limit?: number }): Promise<{
    tools: ToolDefinition[];
    pagination?: { hasNextPage: boolean; cursor?: string | null };
  }> {
    const params = new URLSearchParams();
    if (opts?.status) params.set('status', opts.status);
    if (opts?.cursor) params.set('cursor', opts.cursor);
    if (opts?.limit !== undefined) params.set('limit', String(opts.limit));
    const query = params.toString();
    const url = query ? `/partner/tool-definitions?${query}` : '/partner/tool-definitions';
    const response = await this.request<
      ApiResponse<{
        tools: ToolDefinitionWire[];
        pagination?: { hasNextPage: boolean; cursor?: string | null };
      }>
    >(url);
    return {
      tools: response.data.tools.map(fromWireToolDefinition),
      pagination: response.data.pagination,
    };
  }

  async getTool(id: string): Promise<ToolDefinition> {
    const response = await this.request<ApiResponse<ToolDefinitionWire>>(
      `/partner/tool-definitions/${id}`,
    );
    return fromWireToolDefinition(response.data);
  }

  async createTool(body: ToolDefinitionInput): Promise<ToolDefinition> {
    const response = await this.request<ApiResponse<ToolDefinitionWire>>('/partner/tool-definitions', {
      method: 'POST',
      body: JSON.stringify(toWireToolDefinitionInput(body)),
    });
    return fromWireToolDefinition(response.data);
  }

  async updateTool(id: string, patch: Partial<ToolDefinitionInput>): Promise<ToolDefinition> {
    const response = await this.request<ApiResponse<ToolDefinitionWire>>(
      `/partner/tool-definitions/${id}`,
      {
        method: 'PATCH',
        body: JSON.stringify(toWireToolDefinitionInputPatch(patch)),
      },
    );
    return fromWireToolDefinition(response.data);
  }

  async archiveTool(id: string): Promise<void> {
    await this.request<void>(`/partner/tool-definitions/${id}`, { method: 'DELETE' });
  }

  async testTool(id: string, request: ToolTestRequest): Promise<{ result: ToolTestResult }> {
    const response = await this.request<ApiResponse<{ result: ToolTestResult }>>(
      `/partner/tool-definitions/${id}/test-runs`,
      {
        method: 'POST',
        body: JSON.stringify(request),
      },
    );
    return response.data;
  }

  // Partner credentials
  async getCredentials(): Promise<{ credentials: PartnerCredentialMetadata[] }> {
    const response =
      await this.request<ApiResponse<{ credentials: PartnerCredentialMetadataWire[] }>>(
        '/partner/credentials',
      );
    return { credentials: response.data.credentials.map(fromWirePartnerCredentialMetadata) };
  }

  async createCredential(body: {
    credentialType: 'api_key';
    name: string;
    value: string;
  }): Promise<PartnerCredentialMetadata> {
    const response = await this.request<ApiResponse<PartnerCredentialMetadataWire>>(
      '/partner/credentials',
      {
        method: 'POST',
        body: JSON.stringify(body),
      },
    );
    return fromWirePartnerCredentialMetadata(response.data);
  }

  async deleteCredential(id: string): Promise<void> {
    await this.request<void>(`/partner/credentials/${id}`, { method: 'DELETE' });
  }
}

export type ToolExecutionKind = 'http' | 'schema_lookup';

export interface ToolDefinitionWireBase {
  id: string;
  partnerId: string;
  name: string;
  slug: string;
  description: string;
  executionKind: ToolExecutionKind;
  requiredScope?: string;
  annotations?: Record<string, unknown> | null;
  status: 'active' | 'inactive';
  createdAt: string | null;
  updatedAt: string | null;
}

export interface HttpToolDefinitionWire extends ToolDefinitionWireBase {
  executionKind: 'http';
  lookupConfig: null;
  httpMethod: string;
  baseUrl: string;
  pathTemplate: string;
  authType: string;
  authConfig?: Record<string, unknown>;
  credentials?: Record<string, string>;
  requestSchema?: Record<string, unknown>;
  requestMapping?: Record<string, unknown>;
  responseMapping?: Record<string, unknown> | null;
  schemaMappingRefs?: Record<string, string> | null;
  cacheable?: boolean;
  cacheTtlMs?: number;
  claimEnforcement?: Record<string, string> | null;
  timeoutMs?: number;
  retryPolicy?: Record<string, unknown> | null;
  variables?: Record<string, unknown> | null;
}

export interface SchemaLookupToolDefinitionWire extends ToolDefinitionWireBase {
  executionKind: 'schema_lookup';
  lookupConfig: {
    schemaDefinitionId: string;
    filters: ToolLookupFilter[];
    limit: number;
  };
}

export type ToolDefinitionWire = HttpToolDefinitionWire | SchemaLookupToolDefinitionWire;

export interface ToolDefinitionInputWire {
  slug?: string;
  description?: string;
  executionKind?: ToolExecutionKind;
  lookupConfig?: {
    schemaDefinitionId: string;
    filters: ToolLookupFilter[];
    limit: number;
  };
  httpMethod?: string;
  baseUrl?: string;
  pathTemplate?: string;
  authType?: string;
  authConfig?: Record<string, unknown>;
  credentials?: Record<string, string>;
  requestSchema?: Record<string, unknown>;
  requestMapping?: Record<string, unknown>;
  responseMapping?: Record<string, unknown> | null;
  schemaMappingRefs?: Record<string, string> | null;
  requiredScope?: string;
  cacheable?: boolean;
  cacheTtlMs?: number;
  claimEnforcement?: Record<string, string> | null;
  timeoutMs?: number;
  retryPolicy?: Record<string, unknown> | null;
  annotations?: Record<string, unknown> | null;
  variables?: Record<string, unknown> | null;
  status?: 'active' | 'inactive';
}

interface PartnerCredentialMetadataWire {
  id: string;
  credentialType: 'api_key';
  name: string | null;
  createdAt: string | null;
  rotatedAt: string | null;
  expiresAt: string | null;
}

/**
 * The one place HTTP-only tool fields are enumerated: dashboard key → partner API key.
 * `satisfies` fails the build if a new field is added to `HttpToolDefinition` without a
 * wire name, so serialization and diffing can never drift out of sync with the model.
 */
export const HTTP_TOOL_FIELD_KEYS = {
  http_method: 'httpMethod',
  base_url: 'baseUrl',
  path_template: 'pathTemplate',
  auth_type: 'authType',
  auth_config: 'authConfig',
  credentials: 'credentials',
  request_schema: 'requestSchema',
  request_mapping: 'requestMapping',
  response_mapping: 'responseMapping',
  schema_mapping_refs: 'schemaMappingRefs',
  cacheable: 'cacheable',
  cache_ttl_ms: 'cacheTtlMs',
  claim_enforcement: 'claimEnforcement',
  timeout_ms: 'timeoutMs',
  retry_policy: 'retryPolicy',
  variables: 'variables',
} as const satisfies Record<HttpToolField, keyof ToolDefinitionInputWire>;

export type HttpToolField = keyof Omit<HttpToolDefinitionInput, keyof ToolDefinitionInputBase>;

export const HTTP_TOOL_FIELDS = Object.keys(HTTP_TOOL_FIELD_KEYS) as HttpToolField[];

/** Picks whichever HTTP fields the draft actually carries; a lookup draft yields `{}`. */
const toHttpWireFields = (input: Partial<ToolDefinitionInput>): ToolDefinitionInputWire => {
  const httpInput = input as Partial<HttpToolDefinitionInput>;
  return Object.fromEntries(
    HTTP_TOOL_FIELDS.filter(field => field in httpInput).map(field => [
      HTTP_TOOL_FIELD_KEYS[field],
      httpInput[field],
    ]),
  ) as ToolDefinitionInputWire;
};

/** Converts the partner API's camel-case tool union into the dashboard's local model. */
export const fromWireToolDefinition = (tool: ToolDefinitionWire): ToolDefinition => {
  const common = {
    id: tool.id,
    partner_id: tool.partnerId,
    name: tool.name,
    display_name: tool.slug,
    description: tool.description,
    required_scope: tool.requiredScope ?? 'partner_user',
    annotations: tool.annotations ?? null,
    status: tool.status,
    created_at: tool.createdAt,
    updated_at: tool.updatedAt,
  };

  if (tool.executionKind === 'schema_lookup') {
    return {
      ...common,
      execution_kind: 'schema_lookup',
      lookup_config: {
        schema_definition_id: tool.lookupConfig.schemaDefinitionId,
        filters: tool.lookupConfig.filters,
        limit: tool.lookupConfig.limit,
      },
    };
  }

  return {
    ...common,
    execution_kind: 'http',
    lookup_config: null,
    http_method: tool.httpMethod,
    base_url: tool.baseUrl,
    path_template: tool.pathTemplate,
    auth_type: tool.authType,
    auth_config: tool.authConfig ?? {},
    credentials: tool.credentials ?? {},
    request_schema: tool.requestSchema ?? {},
    request_mapping: tool.requestMapping ?? {},
    response_mapping: tool.responseMapping ?? null,
    schema_mapping_refs: tool.schemaMappingRefs ?? null,
    cacheable: tool.cacheable ?? false,
    cache_ttl_ms: tool.cacheTtlMs ?? 60000,
    claim_enforcement: tool.claimEnforcement ?? null,
    timeout_ms: tool.timeoutMs ?? 5000,
    retry_policy: tool.retryPolicy ?? null,
    variables: tool.variables ?? null,
  };
};

/** Serializes a complete dashboard draft without leaking fields from the other execution kind. */
export const toWireToolDefinitionInput = (input: ToolDefinitionInput): ToolDefinitionInputWire => {
  const common = {
    slug: input.display_name,
    description: input.description,
    executionKind: input.execution_kind,
    requiredScope: input.required_scope,
    annotations: input.annotations,
    status: input.status,
  };

  if (input.execution_kind === 'schema_lookup') {
    return {
      ...common,
      lookupConfig: {
        schemaDefinitionId: input.lookup_config.schema_definition_id,
        filters: input.lookup_config.filters,
        limit: input.lookup_config.limit,
      },
    };
  }

  return { ...common, ...toHttpWireFields(input) };
};

/** Serializes an update patch while preserving omitted fields and stripping derived lookup state. */
export const toWireToolDefinitionInputPatch = (
  input: Partial<ToolDefinitionInput>,
): ToolDefinitionInputWire => {
  const wire: ToolDefinitionInputWire = {
    slug: input.display_name,
    description: input.description,
    executionKind: input.execution_kind,
    requiredScope: input.required_scope,
    annotations: input.annotations,
    status: input.status,
    ...toHttpWireFields(input),
  };

  if ('lookup_config' in input && input.lookup_config !== undefined) {
    wire.lookupConfig = {
      schemaDefinitionId: input.lookup_config.schema_definition_id,
      filters: input.lookup_config.filters,
      limit: input.lookup_config.limit,
    };
  }

  return Object.fromEntries(
    Object.entries(wire).filter(([, value]) => value !== undefined),
  ) as ToolDefinitionInputWire;
};

const fromWirePartnerCredentialMetadata = (
  credential: PartnerCredentialMetadataWire,
): PartnerCredentialMetadata => ({
  id: credential.id,
  credential_type: credential.credentialType,
  name: credential.name,
  created_at: credential.createdAt,
  rotated_at: credential.rotatedAt,
  expires_at: credential.expiresAt,
});

interface ToolDefinitionBase {
  id: string;
  partner_id: string;
  name: string;
  display_name: string;
  description: string;
  execution_kind: ToolExecutionKind;
  required_scope: string;
  annotations: Record<string, unknown> | null;
  status: 'active' | 'inactive';
  created_at: string | null;
  updated_at: string | null;
}

export interface HttpToolDefinition extends ToolDefinitionBase {
  execution_kind: 'http';
  lookup_config: null;
  http_method: string;
  base_url: string;
  path_template: string;
  auth_type: string;
  auth_config: Record<string, unknown>;
  credentials: Record<string, string>;
  request_schema: Record<string, unknown>;
  request_mapping: Record<string, unknown>;
  response_mapping: Record<string, unknown> | null;
  schema_mapping_refs: Record<string, string> | null;
  cacheable: boolean;
  cache_ttl_ms: number;
  claim_enforcement: Record<string, string> | null;
  timeout_ms: number;
  retry_policy: Record<string, unknown> | null;
  variables: Record<string, unknown> | null;
}

export interface ToolLookupFilter {
  field: string;
  value: string | number | boolean;
}

export interface ToolLookupConfigInput {
  schema_definition_id: string;
  filters: ToolLookupFilter[];
  limit: number;
}

export interface SchemaLookupToolDefinition extends ToolDefinitionBase {
  execution_kind: 'schema_lookup';
  lookup_config: ToolLookupConfigInput;
}

export type ToolDefinition = HttpToolDefinition | SchemaLookupToolDefinition;

export interface ToolDefinitionInputBase {
  display_name: string;
  description: string;
  execution_kind: ToolExecutionKind;
  required_scope: string;
  annotations: Record<string, unknown> | null;
  status: 'active' | 'inactive';
}

export type HttpToolDefinitionInput = Omit<
  HttpToolDefinition,
  'id' | 'partner_id' | 'name' | 'lookup_config' | 'created_at' | 'updated_at'
>;

export interface SchemaLookupToolDefinitionInput extends ToolDefinitionInputBase {
  execution_kind: 'schema_lookup';
  lookup_config: ToolLookupConfigInput;
}

export type ToolDefinitionInput = HttpToolDefinitionInput | SchemaLookupToolDefinitionInput;

export interface ToolTestRequest {
  businessId: string;
  partnerUserId?: string;
  participantPhone?: string;
  voiceCallId?: string;
  scope?: 'partner_user' | 'partner_user:verified';
  args: Record<string, unknown>;
}

export type ToolTestResult =
  | { status: 'success'; data: unknown }
  | { status: 'upstream_http_error'; httpStatus: number; body: unknown }
  | {
      error:
        | 'NOT_FOUND'
        | 'VALIDATION_ERROR'
        | 'CLAIM_MISMATCH'
        | 'UPGRADE_REQUIRED'
        | 'UPSTREAM_TIMEOUT'
        | 'UPSTREAM_ERROR'
        | 'AUTH_FAILURE'
        | 'CONFIGURATION_ERROR';
      message?: string;
      requiredScope?: string;
      fields?: string[];
    };

export interface PartnerCredentialMetadata {
  id: string;
  credential_type: 'api_key';
  name: string | null;
  created_at: string | null;
  rotated_at: string | null;
  expires_at: string | null;
}

/** Shortened id for credentials with no name (rows created before names existed). */
export const truncateCredentialId = (id: string): string => (id.length > 12 ? `${id.slice(0, 8)}…` : id);

/** The label partners see for a credential — their name, or a truncated-id fallback. */
export const credentialDisplayName = (
  credential: Pick<PartnerCredentialMetadata, 'id' | 'name'>,
): string => credential.name || `Credential ${truncateCredentialId(credential.id)}`;

// Communications types
export interface CommunicationGroup {
  communicationGroupId: string;
  communicationId: string;
  type?: 'email' | 'sms';
  communicationType?: 'email' | 'sms';
  // Email fields
  compiledHtml?: string;
  emailSubject?: string;
  emailFromEmail?: string | null;
  emailFromName?: string | null;
  emailReplyTo?: string | null;
  emailPreviewText?: string | null;
  openedCount?: number;
  clickedCount?: number;
  engagementRate?: string;
  clickRate?: string;
  attributedRevenue?: string | null;
  // SMS fields
  messageBody?: string;
  companyName?: string;
  recipientPhone?: string;
  // Common fields
  automationName?: string;
  communicationCreatedAt: string;
  businessId: string;
  businessName: string;
  businessDeletedAt?: string | null;
  platformId: string;
  platformName: string;
  automationId?: string;
  automationType?: string;
  recipientCount: number;
  deliveredCount: number;
  deliveryRate?: string;
  includedSegments?: string;
  excludedSegments?: string;
  includeSegmentCount?: number;
  excludeSegmentCount?: number;
}

export interface CommunicationsFilters {
  type?: 'email' | 'sms' | 'all';
  page?: number;
  limit?: number;
  platforms?: string;
  businesses?: string;
  automationType?: string;
  startDate?: string;
  endDate?: string;
  delivered?: string;
  opened?: string;
  clicked?: string;
  search?: string;
}

export interface CommunicationsPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface CommunicationsResponse {
  communications: CommunicationGroup[];
  pagination: CommunicationsPagination;
}

export interface Platform {
  id: string;
  name: string;
  count?: number;
}

export interface Business {
  id: string;
  name: string;
  platformId?: string;
  count?: number;
}

export interface AutomationType {
  type: string;
  label: string;
  count: number;
}

export interface CommunicationRecipient {
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  delivered: boolean | null;
  opened: boolean | null;
  clicked: boolean | null;
  createdAt: string | null;
}

export interface CommunicationRecipientsPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface CommunicationRecipientsResponse {
  recipients: CommunicationRecipient[];
  pagination: CommunicationRecipientsPagination;
}

export interface BatchesResponse {
  success: boolean;
  message?: string;
  data: {
    results: PartnerBatchStatusResponse[];
    pagination: {
      total: number | null;
      hasNextPage: boolean;
      cursor: string | null;
    };
  };
}

export const partnerApi = new PartnerApiClient();
